import {
  applyMove,
  cloneState,
  createInitialState,
  generateLegalMoves,
  isGameOver,
  legalMovesFrom,
} from './rules';
import type { GameState, Move, MoveResult } from './types';

export type GameEvent =
  | { type: 'move'; result: MoveResult }
  | { type: 'status'; state: GameState }
  | { type: 'pause'; paused: boolean };

export class GameEngine {
  state: GameState;
  onEvent?: (event: GameEvent) => void;

  constructor(seed?: number) {
    this.state = createInitialState(seed);
  }

  newGame(seed?: number): void {
    this.state = createInitialState(seed);
    this.state.paused = false;
    this.onEvent?.({ type: 'status', state: this.state });
  }

  restore(state: GameState): void {
    this.state = cloneState(state);
    this.state.paused = true;
    this.onEvent?.({ type: 'status', state: this.state });
  }

  togglePause(force?: boolean): void {
    this.state.paused = force ?? !this.state.paused;
    this.onEvent?.({ type: 'pause', paused: this.state.paused });
  }

  legalMoves(): Move[] {
    return generateLegalMoves(this.state);
  }

  legalMovesFrom(square: string): Move[] {
    return legalMovesFrom(this.state, square);
  }

  move(move: Move): MoveResult {
    if (this.state.paused) return { ok: false, reason: 'GAME PAUSED' };
    if (isGameOver(this.state)) return { ok: false, reason: 'GAME OVER' };
    const result = applyMove(this.state, move);
    if (!result.ok || !result.state) return result;
    this.state = result.state;
    this.onEvent?.({ type: 'move', result });
    this.onEvent?.({ type: 'status', state: this.state });
    return result;
  }

  moveWhilePaused(move: Move): MoveResult {
    const wasPaused = this.state.paused;
    this.state.paused = false;
    const result = this.move(move);
    this.state.paused = wasPaused;
    return result;
  }
}
